import React, { useState, useEffect, useRef } from 'react';
import { css } from '@emotion/css';
import SelectField from './SelectField';
import { color_shades_lighter, color_shades_dark, color_colors_ocean, color_text_light } from '../../constants/colors';
import { TimeRange, DaySchedule, convert12HourTo24Hour, convert24HourTo12Hour } from '../../utils/weeklyScheduleFormatters';

const addIcon = require('../../content/images/add-circle.svg');
const deleteIcon = require('../../content/images/trash.svg');

interface TimeRangeFieldProps {
    name?: string;
    label?: string;
    value?: string; // JSON string of DaySchedule[]
    onChange?: (value: string) => void;
    onBlur?: () => void;
    disabled?: boolean;
    className?: string;
    error?: string;
}

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// Modern select styles
const modernSelectStyle = css`
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    width: 100%;
    min-width: 140px;
    margin-bottom: 0;
    
    /* Override label container height */
    .css-og1717-labelcontainer {
        height: 0;
        display: none;
    }
    
    .Select-control {
        border: 1px solid ${color_shades_dark};
        border-radius: 6px;
        min-height: 44px;
        background: white;
        transition: all 0.2s ease;
        
        &:hover {
            border-color: ${color_colors_ocean};
        }
    }
    
    &.is-focused:not(.is-open) .Select-control {
        border-color: ${color_colors_ocean};
        box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
    }
    
    .Select-placeholder,
    .Select-value {
        padding: 11px 14px;
        font-size: 14px;
        line-height: 1.4;
        display: flex;
        align-items: center;
    }
    
    .Select-value-label {
        color: ${color_text_light};
    }
    
    .Select-input {
        padding: 11px 14px;
        height: 44px;
    }
    
    .Select-input > input {
        padding: 0;
        line-height: 1.4;
        font-size: 14px;
        color: ${color_text_light};
    }
    
    .Select-menu-outer {
        border: 1px solid ${color_shades_dark};
        border-radius: 6px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        margin-top: 4px;
        z-index: 1000;
    }
    
    .Select-menu {
        max-height: 240px;
        border-radius: 6px;
    }
    
    .Select-option {
        padding: 10px 12px;
        font-size: 14px;
        cursor: pointer;
        transition: background 0.15s ease;
        
        &.is-focused {
            background: #f0f4f8;
            color: ${color_text_light};
        }
        
        &.is-selected {
            background: ${color_colors_ocean};
            color: white;
        }
    }
    
    .Select-arrow-zone {
        padding-right: 8px;
    }
    
    .Select-arrow {
        border-color: ${color_text_light} transparent transparent;
        border-width: 5px 4px 2.5px;
    }
    
    &.is-open .Select-arrow {
        border-color: transparent transparent ${color_text_light};
        border-width: 0 4px 5px;
    }
`;

const modernStatusSelectStyle = css`
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    margin-bottom: 0;
    
    /* Override label container height */
    .css-og1717-labelcontainer {
        height: 0;
        display: none;
    }
    
    .Select-control {
        border: 1px solid ${color_shades_dark};
        border-radius: 6px;
        min-height: 44px;
        background: white;
        transition: all 0.2s ease;
        
        &:hover {
            border-color: ${color_colors_ocean};
        }
    }
    
    &.is-focused:not(.is-open) .Select-control {
        border-color: ${color_colors_ocean};
        box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
    }
    
    .Select-placeholder,
    .Select-value {
        padding: 11px 12px;
        font-size: 14px;
        line-height: 1.4;
        display: flex;
        align-items: center;
    }
    
    .Select-value-label {
        color: ${color_text_light};
    }
    
    .Select-input {
        padding: 11px 12px;
        height: 44px;
    }
    
    .Select-input > input {
        padding: 0 0 0 2px;
        line-height: 1.4;
        font-size: 14px;
        color: ${color_text_light};
    }
    
    .Select-menu-outer {
        border: 1px solid ${color_shades_dark};
        border-radius: 6px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        margin-top: 4px;
    }
    
    .Select-menu {
        max-height: 200px;
        border-radius: 6px;
    }
    
    .Select-option {
        padding: 8px 12px;
        font-size: 14px;
        cursor: pointer;
        transition: background 0.15s ease;
        
        &.is-focused {
            background: #f0f4f8;
            color: ${color_text_light};
        }
        
        &.is-selected {
            background: ${color_colors_ocean};
            color: white;
        }
    }
    
    .Select-arrow-zone {
        padding-right: 8px;
    }
    
    .Select-arrow {
        border-color: ${color_text_light} transparent transparent;
        border-width: 5px 4px 2.5px;
    }
    
    &.is-open .Select-arrow {
        border-color: transparent transparent ${color_text_light};
        border-width: 0 4px 5px;
    }
`;

const TimeRangeField: React.FC<TimeRangeFieldProps> = (props) => {
    // Parse the value from backend format or JSON string
    const parseValue = (val: string | undefined): DaySchedule[] => {
        if (!val) {
            // Initialize with all days closed
            return DAYS_OF_WEEK.map(day => ({
                day,
                isOpen: false,
                timeRanges: []
            }));
        }

        try {
            const parsed = JSON.parse(val);

            // Check if it's backend format (array of 7 strings with OPEN hours)
            if (Array.isArray(parsed) && parsed.length === 7 && typeof parsed[0] === 'string') {
                return DAYS_OF_WEEK.map((day, index) => {
                    const dayString = parsed[index];

                    // Check if closed all day (empty string or 'closed')
                    if (!dayString || dayString === 'closed') {
                        return { day, isOpen: false, timeRanges: [] };
                    }

                    // Parse open periods from backend
                    const openTimeRanges: TimeRange[] = [];
                    const ranges = dayString.split('|');

                    for (const range of ranges) {
                        const [start, end] = range.split(',');
                        if (start && end) {
                            openTimeRanges.push({
                                id: `time_${Date.now()}_${openTimeRanges.length}`,
                                startTime: convert24HourTo12Hour(start.trim()),
                                endTime: convert24HourTo12Hour(end.trim())
                            });
                        }
                    }

                    return {
                        day,
                        isOpen: openTimeRanges.length > 0,
                        timeRanges: openTimeRanges
                    };
                });
            }

            // Otherwise, assume it's the frontend format (array of DaySchedule objects)
            return DAYS_OF_WEEK.map(day => {
                const existing = parsed.find((d: DaySchedule) => d.day === day);
                return existing || { day, isOpen: false, timeRanges: [] };
            });
        } catch {
            return DAYS_OF_WEEK.map(day => ({
                day,
                isOpen: false,
                timeRanges: []
            }));
        }
    };

    const [schedule, setSchedule] = useState<DaySchedule[]>(parseValue(props.value));

    // Ref to prevent infinite loops from internal updates
    const internalUpdateRef = useRef<boolean>(false);

    // Sync schedule with external prop changes
    useEffect(() => {
        // Ignore updates originating from our own onChange
        if (internalUpdateRef.current) {
            internalUpdateRef.current = false;
            return;
        }

        const newSchedule = parseValue(props.value);
        setSchedule(newSchedule);
    }, [props.value]);

    // Generate time options in 30-minute increments
    const generateTimeOptions = () => {
        const times: { value: string; label: string }[] = [];
        const periods = ['AM', 'PM'];

        for (const period of periods) {
            for (let hour = 12; hour >= 1; hour--) {
                for (const minute of ['00', '30']) {
                    const value = `${hour}:${minute} ${period}`;
                    times.push({ value, label: value });
                }
            }
        }

        // Sort times properly
        return times.sort((a, b) => {
            const parseTime = (timeStr: string) => {
                const [time, period] = timeStr.split(' ');
                const [hour, minute] = time.split(':').map(Number);
                let hours24 = hour === 12 ? 0 : hour;
                if (period === 'PM' && hour !== 12) hours24 += 12;
                if (period === 'AM' && hour === 12) hours24 = 0;
                return hours24 * 60 + minute;
            };
            return parseTime(a.value) - parseTime(b.value);
        });
    };

    const timeOptions = generateTimeOptions();

    // Calculate closed hours from open hours
    const calculateClosedHours = (openTimeRanges: TimeRange[]): string => {
        if (openTimeRanges.length === 0) {
            return 'Closed All Day';
        }

        const closedPeriods: string[] = [];
        const sortedRanges = [...openTimeRanges].sort((a, b) => {
            const aStart = convert12HourTo24Hour(a.startTime);
            const bStart = convert12HourTo24Hour(b.startTime);
            return aStart.localeCompare(bStart);
        });

        // Check if closed from midnight to first opening
        const firstStart = convert12HourTo24Hour(sortedRanges[0].startTime);
        if (firstStart !== '00:00') {
            closedPeriods.push(`${convert24HourTo12Hour('00:00')}-${convert24HourTo12Hour(firstStart)}`);
        }

        // Check gaps between open periods
        for (let i = 0; i < sortedRanges.length - 1; i++) {
            const currentEnd = convert12HourTo24Hour(sortedRanges[i].endTime);
            const nextStart = convert12HourTo24Hour(sortedRanges[i + 1].startTime);
            if (currentEnd !== nextStart) {
                closedPeriods.push(`${convert24HourTo12Hour(currentEnd)}-${convert24HourTo12Hour(nextStart)}`);
            }
        }

        // Check if closed from last closing to end of day
        const lastEnd = convert12HourTo24Hour(sortedRanges[sortedRanges.length - 1].endTime);
        // End of day is 23:59, but if they close at anything before that, show the closed period
        if (lastEnd < '23:59') {
            closedPeriods.push(`${convert24HourTo12Hour(lastEnd)}-${convert24HourTo12Hour('23:59')}`);
        }

        return closedPeriods.length > 0 ? `Closed: ${closedPeriods.join(', ')}` : 'Open 24 Hours';
    };

    // Convert schedule to backend format: Array of 7 strings with OPEN hours
    const convertToBackendFormat = (schedule: DaySchedule[]): string[] => {
        return DAYS_OF_WEEK.map(dayName => {
            const daySchedule = schedule.find(d => d.day === dayName);

            if (!daySchedule || !daySchedule.isOpen || daySchedule.timeRanges.length === 0) {
                // Closed all day = empty string
                return '';
            }

            // Sort time ranges by start time
            const sortedRanges = [...daySchedule.timeRanges].sort((a, b) => {
                const aStart = convert12HourTo24Hour(a.startTime);
                const bStart = convert12HourTo24Hour(b.startTime);
                return aStart.localeCompare(bStart);
            });

            // Convert each open period to 24-hour format
            const openPeriods = sortedRanges.map(range => {
                const start = convert12HourTo24Hour(range.startTime);
                const end = convert12HourTo24Hour(range.endTime);
                return `${start},${end}`;
            });

            return openPeriods.join('|');
        });
    };

    const updateValue = (newSchedule: DaySchedule[]) => {
        setSchedule(newSchedule);
        if (props.onChange) {
            // Set ref before calling onChange to prevent useEffect from reacting
            internalUpdateRef.current = true;
            // Convert to backend format (array of 7 strings)
            const backendFormat = convertToBackendFormat(newSchedule);
            props.onChange(JSON.stringify(backendFormat));
        }
    };

    // Validate that open days have at least one time range
    const getValidationError = () => {
        for (const day of schedule) {
            // Check if open day has time ranges
            if (day.isOpen && day.timeRanges.length === 0) {
                return `${day.day}: Please add hours or mark as closed.`;
            }

            // Check for overlapping time ranges
            if (day.isOpen && day.timeRanges.length > 1) {
                const sortedRanges = [...day.timeRanges].sort((a, b) => {
                    const aStart = convert12HourTo24Hour(a.startTime);
                    const bStart = convert12HourTo24Hour(b.startTime);
                    return aStart.localeCompare(bStart);
                });

                for (let i = 0; i < sortedRanges.length - 1; i++) {
                    const currentEnd = convert12HourTo24Hour(sortedRanges[i].endTime);
                    const nextStart = convert12HourTo24Hour(sortedRanges[i + 1].startTime);

                    if (currentEnd > nextStart) {
                        return `${day.day}: Time ranges cannot overlap.`;
                    }
                }
            }

            // Check that end time is after start time
            for (const range of day.timeRanges) {
                const start = convert12HourTo24Hour(range.startTime);
                const end = convert12HourTo24Hour(range.endTime);
                if (start >= end) {
                    return `${day.day}: End time must be after start time.`;
                }
            }
        }

        return null;
    };

    const validationError = getValidationError();

    const handleToggleOpen = (day: string, isOpen: boolean) => {
        updateValue(schedule.map(d =>
            d.day === day ? {
                ...d,
                isOpen,
                timeRanges: isOpen && d.timeRanges.length === 0
                    ? [{ id: `time_${Date.now()}_${Math.floor(Math.random() * 1e6)}`, startTime: '9:00 AM', endTime: '5:00 PM' }]
                    : d.timeRanges
            } : d
        ));
    };

    const handleAddTimeRange = (day: string) => {
        const newTimeRange: TimeRange = {
            id: `time_${Date.now()}_${Math.floor(Math.random() * 1e6)}`,
            startTime: '9:00 AM',
            endTime: '5:00 PM'
        };
        updateValue(schedule.map(d =>
            d.day === day ? { ...d, timeRanges: [...d.timeRanges, newTimeRange] } : d
        ));
    };

    const handleRemoveTimeRange = (day: string, timeRangeId: string) => {
        updateValue(schedule.map(d =>
            d.day === day ? { ...d, timeRanges: d.timeRanges.filter(tr => tr.id !== timeRangeId) } : d
        ));
    };

    const handleTimeRangeChange = (day: string, timeRangeId: string, field: 'startTime' | 'endTime', value: string) => {
        updateValue(schedule.map(d =>
            d.day === day ? {
                ...d,
                timeRanges: d.timeRanges.map(tr =>
                    tr.id === timeRangeId ? { ...tr, [field]: value } : tr
                )
            } : d
        ));
    };

    return (
        <div className={`${containerStyle} ${props.className || ''}`}>
            {props.label && <label className={labelStyle}>{props.label}</label>}
            {validationError && <div className={errorStyle}>{validationError}</div>}

            <div className={daysContainer}>
                {schedule.map((daySchedule) => (
                    <div key={daySchedule.day} className={dayCard}>
                        <div className={dayHeader}>
                            <span className={dayLabel}>{daySchedule.day.toUpperCase()}</span>
                            <div className={statusSelectWrapper}>
                                <SelectField
                                    name={`${props.name}_${daySchedule.day}_status`}
                                    value={daySchedule.isOpen ? 'Open' : 'Closed'}
                                    onChange={(selected: any) =>
                                        handleToggleOpen(daySchedule.day, selected?.value === 'Open')
                                    }
                                    options={[
                                        { value: 'Open', label: 'Open' },
                                        { value: 'Closed', label: 'Closed' }
                                    ]}
                                    disabled={props.disabled}
                                    clearable={false}
                                    useForm={false}
                                    customClassName={modernStatusSelectStyle}
                                    hideLabel={true}
                                />
                                {daySchedule.isOpen && daySchedule.timeRanges.length > 0 && (
                                    <div className={closedHoursInfo}>
                                        {calculateClosedHours(daySchedule.timeRanges)}
                                    </div>
                                )}
                            </div>
                        </div>

                        {daySchedule.isOpen && (
                            <div className={timeRangesContainer}>
                                <div className={openHoursLabel}>Open Hours:</div>
                                {daySchedule.timeRanges.map((timeRange) => (
                                    <div key={timeRange.id} className={timeRangeRow}>
                                        <div className={timeRangeFields}>
                                            <SelectField
                                                name={`${props.name}_${daySchedule.day}_start_${timeRange.id}`}
                                                placeholder="Start time"
                                                value={timeRange.startTime}
                                                onChange={(selected: any) =>
                                                    handleTimeRangeChange(daySchedule.day, timeRange.id, 'startTime', selected?.value || '')
                                                }
                                                options={timeOptions}
                                                disabled={props.disabled}
                                                clearable={false}
                                                useForm={false}
                                                customClassName={modernSelectStyle}
                                                hideLabel={true}
                                            />
                                            <span className={timeSeparator}>—</span>
                                            <SelectField
                                                name={`${props.name}_${daySchedule.day}_end_${timeRange.id}`}
                                                placeholder="End time"
                                                value={timeRange.endTime}
                                                onChange={(selected: any) =>
                                                    handleTimeRangeChange(daySchedule.day, timeRange.id, 'endTime', selected?.value || '')
                                                }
                                                options={timeOptions}
                                                disabled={props.disabled}
                                                clearable={false}
                                                useForm={false}
                                                customClassName={modernSelectStyle}
                                                hideLabel={true}
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            className={deleteButton}
                                            onClick={() => handleRemoveTimeRange(daySchedule.day, timeRange.id)}
                                            disabled={props.disabled}
                                            title="Remove time range"
                                        >
                                            <img src={deleteIcon} alt="Remove" />
                                        </button>
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    className={addTimeRangeButton}
                                    onClick={() => handleAddTimeRange(daySchedule.day)}
                                    disabled={props.disabled}
                                >
                                    <img src={addIcon} alt="Add" />
                                    Add time range
                                </button>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

const containerStyle = css`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const labelStyle = css`
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 12px;
    color: ${color_text_light};
`;

const errorStyle = css`
    font-size: 14px;
    color: #d32f2f;
    margin-bottom: 12px;
    padding: 8px 12px;
    background-color: #ffebee;
    border-radius: 4px;
    border-left: 4px solid #d32f2f;
`;

const daysContainer = css`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const dayCard = css`
    border: 1px solid ${color_shades_dark};
    border-radius: 8px;
    padding: 32px;
    background: white;
`;

const dayHeader = css`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 0;
    padding-left: 4px;
`;

const dayLabel = css`
    font-size: 14px;
    font-weight: 600;
    min-width: 100px;
    color: ${color_text_light};
`;

const statusSelectWrapper = css`
    min-width: 140px;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const closedHoursInfo = css`
    font-size: 12px;
    color: ${color_text_light};
    padding: 4px 8px;
    background: #f8f9fa;
    border-radius: 4px;
    font-style: italic;
`;

const timeRangesContainer = css`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding-left: 116px;
    margin-top: 12px;
`;

const openHoursLabel = css`
    font-size: 13px;
    font-weight: 600;
    color: ${color_text_light};
    margin-bottom: 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

const timeRangeRow = css`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const timeRangeFields = css`
    flex: 1;
    display: flex;
    align-items: center;
    gap: 12px;
`;

const timeSeparator = css`
    font-size: 16px;
    color: ${color_text_light};
    flex-shrink: 0;
`;

const deleteButton = css`
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.5;
    transition: opacity 0.2s;
    
    &:hover {
        opacity: 1;
    }
    
    &:disabled {
        cursor: not-allowed;
        opacity: 0.3;
    }
    
    img {
        width: 18px;
        height: 18px;
    }
`;

const addTimeRangeButton = css`
    background: transparent;
    border: 1px dashed ${color_shades_dark};
    border-radius: 4px;
    padding: 8px 12px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: 14px;
    color: ${color_colors_ocean};
    transition: all 0.2s;
    
    &:hover {
        background: #f9f9f9;
        border-color: ${color_colors_ocean};
    }
    
    &:disabled {
        cursor: not-allowed;
        opacity: 0.5;
    }
    
    img {
        width: 16px;
        height: 16px;
    }
`;

export default TimeRangeField;
